import type { SessionMode } from '../../utils/trainingProtocol';
import type { ChallengeTier } from './types';
import { clamp } from './rules';

export interface MatchCard { id: number; pair: number; shape: number; fill: boolean; marked: boolean; }
export interface MatchAttempt {
  first: number;
  second: number | null;
  outcome: 'match' | 'mismatch' | 'timeout';
  pristine: boolean;
  timestamp: number;
}
export interface MatchReplayData {
  cards: MatchCard[];
  complementary: boolean;
  attempts: MatchAttempt[];
}
export const MATCH_SHAPE_NAMES = ['圆形', '菱形', '三角形', '方形', '五边形', '六边形', '星形', '十字形'];

export function getMatchRules(mode: SessionMode, tier: ChallengeTier) {
  const level = mode === 'standard' ? 0 : tier;
  const pairs = [6, 8, 8, 10][level];
  const mistakeLimit = [6, 5, 5, 4][level];
  return {
    level, pairs, columns: 4,
    previewMs: [7000, 6000, 5500, 5000][level],
    timeMs: [120000, 105000, 95000, 90000][level],
    secondCardMs: [0, 0, 7000, 5000][level],
    mistakeLimit, maxAttempts: pairs + mistakeLimit - 1,
    scans: level < 2 ? 1 : 0, scanMs: 1500,
    complementary: level === 3,
    similarFaces: level >= 2,
  };
}

export function createMatchDeck(level: number, pairs: number, random = Math.random): MatchCard[] {
  const cards: MatchCard[] = [];
  for (let pair = 0; pair < pairs; pair++) {
    for (let copy = 0; copy < 2; copy++) {
      cards.push({ id: pair * 2 + copy, pair,
        shape: level === 3 ? pair % 5 : level === 2 ? pair % 4 : pair,
        marked: level === 3 && pair >= 5,
        fill: level === 3 ? copy === 0 : level === 2 ? pair < 4 : true,
      });
    }
  }
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(random() * (i + 1)));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }
  return cards;
}

export function cardsMatch(a: MatchCard, b: MatchCard, complementary: boolean): boolean {
  return a.id !== b.id && a.shape === b.shape && a.marked === b.marked && (complementary ? a.fill !== b.fill : a.fill === b.fill);
}
export function matchCardName(card: MatchCard) {
  return `${card.fill ? '实心' : '空心'}${MATCH_SHAPE_NAMES[card.shape]}${card.marked ? '，带标记' : ''}`;
}
export function canFlipCard(index: number, count: number, first: number | null, matched: ReadonlySet<number>, locked: boolean) {
  return !locked && Number.isInteger(index) && index >= 0 && index < count && index !== first && !matched.has(index);
}

export function summarizeMatch(attempts: MatchAttempt[], pairs: number, scans: number) {
  let combo = 0, maxCombo = 0;
  for (const attempt of attempts) {
    combo = attempt.outcome === 'match' ? combo + 1 : 0;
    maxCombo = Math.max(maxCombo, combo);
  }
  const matched = attempts.filter(a => a.outcome === 'match').length;
  const pristine = attempts.filter(a => a.outcome === 'match' && a.pristine).length;
  const accuracy = attempts.length ? Math.round(matched / attempts.length * 100) : 0;
  const efficiency = matched / Math.max(1, attempts.length);
  const score = Math.round(clamp(60 * matched / pairs + 25 * efficiency * matched / pairs + 10 * pristine / pairs + 5 * maxCombo / pairs - scans * 5, 0, 100));
  return { score, accuracy, matched, pristine, mistakes: attempts.length - matched, combo, maxCombo, efficiency: Math.round(efficiency * 100) };
}
